import React, { createContext, useContext, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { BF } from "../lib/constants";
import { atLeast } from "../lib/roles";

const ClubCtx = createContext(null);

export function useClub() {
  return useContext(ClubCtx);
}

function Loading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: BF, fontSize: 19, color: "#1C1B19", fontWeight: 800 }}>
      LADEN...
    </div>
  );
}

function Message({ text }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo',sans-serif", padding: 24, textAlign: "center", color: "#555" }}>
      {text}
    </div>
  );
}

// Zoekt de slug van de club van de ingelogde gebruiker op (voor redirects naar "je eigen
// club"). undefined = nog aan het laden, null = geen club gekoppeld.
export function useOwnClubSlug() {
  var { profile } = useAuth();
  var [slug, setSlug] = useState(undefined);
  useEffect(function () {
    var cancelled = false;
    async function go() {
      if (!profile || !profile.clubId) { if (!cancelled) setSlug(null); return; }
      try {
        var snap = await getDoc(doc(db, "clubs", profile.clubId));
        if (!cancelled) setSlug(snap.exists() ? snap.data().slug : null);
      } catch (e) {
        console.error("Club lookup error:", e);
        if (!cancelled) setSlug(null);
      }
    }
    go();
    return function () { cancelled = true; };
  }, [profile]);
  return slug;
}

// Scherm voor een aangemelde club die nog op goedkeuring wacht.
export function PendingScreen() {
  var { profile, logout } = useAuth();
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Archivo',sans-serif", padding: 16, background: "#F5F3EE" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,.08)" }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🎾</div>
        <h1 style={{ fontFamily: BF, fontSize: 21, color: "#1C1B19", marginBottom: 8, fontWeight: 800 }}>Aanmelding ontvangen</h1>
        <p style={{ fontSize: 14, color: "#555", lineHeight: 1.6, marginBottom: 18 }}>
          Bedankt{profile && profile.name ? ", " + profile.name.split(" ")[0] : ""}! Je aanmelding wordt beoordeeld. Zodra die is goedgekeurd krijg je een mail en kun je hier inloggen.
        </p>
        <button onClick={function () { logout(); }} style={{ background: "none", border: "1px solid #ccc", borderRadius: 8, padding: "8px 18px", cursor: "pointer", color: "#888", fontSize: 13 }}>Uitloggen</button>
      </div>
    </div>
  );
}

// `guest`: wat niet-ingelogde bezoekers zien (de startpagina); zonder `guest` naar /login.
export function RootRedirect({ guest }) {
  var { user, profile, loading } = useAuth();
  var slug = useOwnClubSlug();
  if (loading) return <Loading />;
  if (!user) return guest || <Navigate to="/login" replace />;
  if (profile && profile.status === "pending") return <PendingScreen />;
  if (slug === undefined) return <Loading />;
  if (slug) return <Navigate to={"/" + slug} replace />;
  return <Message text="Er is nog geen club aan je account gekoppeld. Neem contact op met de beheerder." />;
}

function ForbiddenRedirect() {
  var { profile } = useAuth();
  var slug = useOwnClubSlug();
  if (profile && profile.status === "pending") return <PendingScreen />;
  if (slug === undefined) return <Loading />;
  if (slug) return <Navigate to={"/" + slug} replace />;
  return <Message text="Je hebt geen toegang tot deze club." />;
}

// Beschermt de route /:clubSlug: vereist login, zoekt de club bij de slug op, en
// controleert dat de ingelogde gebruiker ook echt bij die club hoort voordat de
// eigenlijke app (children) gerenderd wordt.
export function ClubGate({ children }) {
  var { clubSlug } = useParams();
  var { user, profile, loading: authLoading } = useAuth();
  var [club, setClub] = useState(null);
  var [status, setStatus] = useState("loading");

  useEffect(function () {
    setStatus("loading");
    setClub(null);
    if (authLoading) return;
    if (!user) { setStatus("unauth"); return; }
    if (!profile) { setStatus("no-profile"); return; }

    var cancelled = false;
    async function resolve() {
      try {
        var q = query(collection(db, "clubs"), where("slug", "==", clubSlug));
        var snap = await getDocs(q);
        if (cancelled) return;
        if (snap.empty) { setStatus("not-found"); return; }
        var clubDoc = snap.docs[0];
        // Een creator mag bij elke club; anderen alleen bij hun eigen club.
        if (clubDoc.id !== profile.clubId && !atLeast(profile, "creator")) { setStatus("forbidden"); return; }
        setClub({ id: clubDoc.id, ...clubDoc.data() });
        setStatus("ok");
      } catch (e) {
        console.error("Club resolve error:", e);
        if (!cancelled) setStatus("not-found");
      }
    }
    resolve();
    return function () { cancelled = true; };
  }, [clubSlug, user, profile, authLoading]);

  if (authLoading || status === "loading") return <Loading />;
  if (status === "unauth" || status === "no-profile") {
    return <Navigate to="/login" replace state={{ from: "/" + clubSlug }} />;
  }
  if (status === "not-found") return <Message text="Deze club bestaat niet." />;
  if (status === "forbidden") return <ForbiddenRedirect />;

  // De Tennis Training Deck (de oefeningen-secties) is alleen voor Atalanta; andere clubs
  // hebben alleen hun eigen "Extra oefeningen". Via het veld trainingDeck op het
  // club-document kan dit later ook voor een andere club aangezet worden.
  var hasDeck = club.trainingDeck === true || club.id === "atalanta";
  // Seizoensplannen worden centraal (bij Atalanta) gemaakt en door alle clubs gedeeld.
  var planClubId = club.planClubId || "atalanta";
  // Losse trainer (build 2.5): een "club" met maar één persoon, dus niets te delen.
  var solo = club.solo === true;
  return <ClubCtx.Provider value={{ clubId: club.id, club: club, hasDeck: hasDeck, planClubId: planClubId, solo: solo }}>{children}</ClubCtx.Provider>;
}
